// File: src/components/forms/TextField/TextField.tsx

import React, { useState, useEffect, forwardRef } from "react";
import styled from "styled-components";
import { LucideIcon } from "lucide-react";
import { Typography } from "../../general/Typography";
import { InputEvent, FocusEvent, KeyboardEvent } from "../types/fieldTypes";

// Props
export interface TextFieldProps {
  id?: string;
  label?: string;
  placeholder?: string;
  type?: "text" | "password" | "email" | "tel" | "number" | "date" | "search" | "url";
  fullWidth?: boolean;
  disabled?: boolean;
  size?: "small" | "medium" | "large";
  value?: string;
  required?: boolean;
  helperText?: string;
  error?: string | undefined | null;
  overline?: string;
  validation?: (value: string) => string | null;
  minLength?: number;
  maxLength?: number;
  multiline?: boolean;
  minRows?: number;
  maxRows?: number;
  icon?: LucideIcon;
  onIconClick?: () => void;
  mask?: (value: string) => string;
  debounce?: number;
  onFocus?: (e: FocusEvent) => void;
  onBlur?: (e: FocusEvent) => void;
  onChange?: (e: InputEvent) => void;
  onKeyDown?: (e: KeyboardEvent) => void;
  autoComplete?: boolean | string;
  min?: string | number;
  max?: string | number;
}

// Styled Components
const Wrapper = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")};
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  background-color: ${({ theme }) => theme.colors.surfaceEntry};
  color: ${({ theme }) => theme.colors.textPrimary};
  outline: none;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  background-color: ${({ theme }) => theme.colors.surfaceEntry};
  color: ${({ theme }) => theme.colors.textPrimary};
  resize: none;
`;

const IconWrapper = styled.div`
  position: absolute;
  right: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const HelperMessage = styled(Typography)`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.caption.fontSize};
  margin-top: ${({ theme }) => theme.spacing.xxs};
`;

const ErrorMessage = styled(Typography)`
  color: ${({ theme }) => theme.colors.error};
  margin-top: ${({ theme }) => theme.spacing.xxs};
`;

const MessageContainer = styled.div`
  min-height: ${({ theme }) => theme.spacing.lg};
  display: flex;
  align-items: center;
`;

// ✅ Main Component
export const TextField = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  TextFieldProps
>(({
  id,
  label,
  placeholder,
  type = "text",
  fullWidth = false,
  disabled = false,
  value = "",
  required = false,
  helperText,
  error,
  overline,
  validation,
  minLength,
  maxLength,
  multiline = false,
  minRows = 2,
  maxRows = 5,
  icon: Icon,
  onIconClick,
  mask,
  onBlur,
  onFocus,
  onChange,
  onKeyDown,
  autoComplete = true,
}, ref) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let newValue = e.target.value;
    if (mask) newValue = mask(newValue);
    setInputValue(newValue);
    setValidationError(validation ? validation(newValue) : null);
    onChange?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (validation) setValidationError(validation(inputValue));
    onBlur?.(e);
  };
  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onFocus?.(e);
  };

  return (
    <Wrapper fullWidth={fullWidth}>
      {label && <Typography as="span" variant="body">{label}</Typography>}
      {overline && <Typography as="span"  variant="overline" colorScheme="textSecondary">{overline}</Typography>}
      <InputContainer>
        {multiline ? (
          <StyledTextarea
            id={id}
            ref={ref as React.Ref<HTMLTextAreaElement>}
            placeholder={placeholder}
            value={inputValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            disabled={disabled}
            required={required}
            minLength={minLength}
            maxLength={maxLength}
            rows={minRows}
            autoComplete={autoComplete ? "on" : "off"}
          />
        ) : (
          <StyledInput
            id={id}
            
            ref={ref as React.Ref<HTMLInputElement>}
            type={type}
            placeholder={placeholder}
            value={inputValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={onKeyDown}
            onFocus={handleFocus}

            disabled={disabled}
            required={required}
            minLength={minLength}
            maxLength={maxLength}
            autoComplete={autoComplete ? type : "off"}
          />
        )}
        {Icon && (
          <IconWrapper onClick={onIconClick} aria-label="icon-action">
            <Icon size={20} />
          </IconWrapper>
        )}
      </InputContainer>
      <MessageContainer>
        {(!error && !validationError && helperText) && <HelperMessage>{helperText}</HelperMessage>}
        {(error || validationError) && <ErrorMessage variant="caption">{error || validationError}</ErrorMessage>}
      </MessageContainer>
    </Wrapper>
  );
});

TextField.displayName = "TextField";